import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ListAlt as StatusIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getLeadStatuses, createLeadStatus, updateLeadStatus, deleteLeadStatus } from '../../services/master';

const LeadStatus = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [leadStatusData, setLeadStatusData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedLeadStatusForDelete, setSelectedLeadStatusForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingLeadStatus, setEditingLeadStatus] = useState(null);
  const [newLeadStatus, setNewLeadStatus] = useState({
    leadStatus: '',
    status: 'Active',
  });

  // Fetch lead statuses
  const fetchLeadStatuses = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const leadStatuses = await getLeadStatuses();
      setLeadStatusData(leadStatuses);
    } catch (err) {
      setApiError('Failed to load lead statuses.');
      setLeadStatusData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  // Filter logic
  const getFilteredLeadStatuses = () => {
    return leadStatusData.filter((ls) => {
      const matchesSearch =
        ls.leadStatus.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || ls.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredLeadStatuses = getFilteredLeadStatuses();

  // Pagination
  const paginatedLeadStatuses = filteredLeadStatuses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newLeadStatus.leadStatus.trim()) err.leadStatus = 'Lead Status is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return leadStatusData.some(
      (ls) =>
        ls.leadStatus.toLowerCase() === newLeadStatus.leadStatus.trim().toLowerCase() &&
        ls.id !== editingLeadStatus?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingLeadStatus(null);
    setNewLeadStatus({
      leadStatus: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingLeadStatus(null);
    setNewLeadStatus({
      leadStatus: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (leadStatus) => {
    setEditingLeadStatus(leadStatus);
    setNewLeadStatus({
      leadStatus: leadStatus.leadStatus,
      status: leadStatus.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (leadStatus) => {
    setSelectedLeadStatusForDelete(leadStatus);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedLeadStatusForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await deleteLeadStatus(selectedLeadStatusForDelete.id);
      setAlertConfig({ title: 'Success', message: 'Lead status deleted successfully!' });
      setAlertOpen(true);
      fetchLeadStatuses(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete lead status.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedLeadStatusForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ leadStatus: 'Lead status already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'leadStatus',
        details: {
          leadStatus: newLeadStatus.leadStatus.trim(),
        },
        status: newLeadStatus.status === 'Active',
      };

      if (editingLeadStatus) {
        await updateLeadStatus(editingLeadStatus.id, payload);
      } else {
        await createLeadStatus(payload);
      }

      setAlertConfig({
        title: 'Success',
        message: editingLeadStatus ? 'Lead status updated successfully!' : 'Lead status added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchLeadStatuses(true);
    } catch (err) {
      setErrors({ leadStatus: 'Failed to save lead status.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchLeadStatuses(true);
  };

  const handleChange = (field, value) => {
    setNewLeadStatus((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Lead Status Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadStatuses()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>
              Add Lead Status
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the lead status <strong>{selectedLeadStatusForDelete?.leadStatus}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadStatuses()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Lead Status..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredLeadStatuses.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} lead statuses matching your criteria.` : 'No lead statuses found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !leadStatusData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Lead Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLeadStatuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No lead statuses found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeadStatuses.map((leadStatus, index) => (
                    <TableRow key={leadStatus.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{leadStatus.leadStatus}</TableCell>
                      <TableCell>
                        <Chip
                          label={leadStatus.status}
                          color={leadStatus.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(leadStatus)} disabled={isLoading}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteClick(leadStatus)} disabled={isLoading}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredLeadStatuses.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLeadStatus ? 'Edit Lead Status' : 'Add New Lead Status'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lead Status"
                  value={newLeadStatus.leadStatus}
                  onChange={(e) => handleChange('leadStatus', e.target.value)}
                  error={!!errors.leadStatus}
                  helperText={errors.leadStatus}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newLeadStatus.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingLeadStatus ? 'Update' : 'Add'} Lead Status
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LeadStatus;